import { ModelType, ModelNamespace, ModelElementPositionMap } from '@view-model/domain/model';
import { StickyModel } from '@view-model/domain/model/StickyModel';
import type { NodeEntityOperation, StickyNode } from '@view-model/models/sticky/StickyNodeView';
import { NodeCollection } from '@view-model/models/sticky/StickyNodeView';
import type { LinkEntity } from '@view-model/models/sticky/StickyLink';
import type { LinkEntityOperation } from '@view-model/models/sticky/StickyLink/adapter/LinkEntityOperation';
import { LinkCollection } from '@view-model/models/sticky/StickyLink/domain/LinkCollection';
import type { StickyZone, StickyZoneEntityOperation } from '@view-model/models/sticky/StickyZoneView';
import { StickyZoneCollection, StickyZoneContents } from '@view-model/models/sticky/StickyZoneView';
import type {
  StickyZoneJSON,
  LinkJSON,
  StickyNodeJSON,
  ModelId,
  StickyZoneId,
  ViewModelAssetId,
  ViewModelId,
} from 'balus-common';
import type { ModelElementId } from '@view-model/domain/key';
import { ModelKey, NodeKey } from '@view-model/domain/key';
import type { ModelCommentContentsJSON } from '@view-model/models/sticky/ModelComment';
import { ModelCommentContents } from '@view-model/models/sticky/ModelComment/domain/ModelCommentContents';
import type { UserKey } from '@user/domain';
import type { IPositionSetRepository, PositionSetJSON } from '@view-model/models/common/PositionSet';
import { PositionSet } from '@view-model/models/common/PositionSet';
import type { ICommand } from '@model-framework/command';
import { CompositeCommand } from '@model-framework/command';
import type { DescriptionPanelCollectionContentsJSON } from '@view-model/models/sticky/DescriptionPanel/domain/DescriptionPanelCollectionContents';
import { DescriptionPanelCollectionContents } from '@view-model/models/sticky/DescriptionPanel/domain/DescriptionPanelCollectionContents';
import type { ElementDescriptionSetJSON } from '@view-model/models/sticky/ElementDescription';
import { ElementDescriptionSet } from '@view-model/models/sticky/ElementDescription';
import type { StickyModelCascadeRepository } from '@view-model/infrastructure/view-model/cascade/StickyModelCascadeRepository';
import { ElementsCreateCommand } from '@view-model/command/basic-model/ElementsCreateCommand';
import type { DisplayOrderTreeJSON } from '@model-framework/display-order';
import { DisplayOrderTree } from '@model-framework/display-order';
import { ElementsMoveCommand } from '@view-model/command/basic-model/ElementsMoveCommand/ElementsMoveCommand';
import type { DisplayOrderRepository } from '@model-framework/display-order/infrastructure';
import type { StickyModelElementPositionMapRepository } from '@view-model/infrastructure/basic-model/StickyModelElementPositionMapRepository';
import type { AssetUrlMap, ViewModelEntity } from '@view-model/domain/view-model';
import type { Point } from '@view-model/models/common/basic';
import { Size } from '@view-model/models/common/basic';
import { Rect } from '@view-model/models/common/basic/Rect';
import { CommentConstants } from '@view-model/models/sticky/ModelComment/constants';
import type { DisplayOrderTreeZone } from '@model-framework/display-order/domain';
import type {
  FileContainerCollectionContentsJSON,
  LoadableFileContainerCollectionContentsJSON,
} from '@view-model/models/sticky/FileContainer/domain/FileContainerCollectionContents';
import { FileContainerCollectionContents } from '@view-model/models/sticky/FileContainer/domain/FileContainerCollectionContents';
import type { FileContainerAssetCopy } from '@view-model/models/sticky/FileContainer/domain/FileContainer';
import type { AnnotationLinkCollectionJSON } from '@view-model/models/sticky/AnnotationLink/domain/AnnotationLinkCollection';
import { AnnotationLinkCollection } from '@view-model/models/sticky/AnnotationLink/domain/AnnotationLinkCollection';

export type StickyModelContentsJSON = {
  key: string;
  type: string;
  namespace: string;
  version: number;
  nodes: StickyNodeJSON[];
  links: LinkJSON[];
  zones: StickyZoneJSON[];
  zonePositions: PositionSetJSON;
  comments: ModelCommentContentsJSON;
  descriptionPanels: DescriptionPanelCollectionContentsJSON;
  fileContainers: FileContainerCollectionContentsJSON;
  annotationLinks: AnnotationLinkCollectionJSON;
  nodeDescriptions: ElementDescriptionSetJSON;
  zoneDescriptions: ElementDescriptionSetJSON;
  displayOrderTree: DisplayOrderTreeJSON;
};

type LoadableContentsJSON = {
  key: string;
  type: string;
  namespace: string;
  version: number;
  nodes: StickyNodeJSON[];
  links: LinkJSON[];
  zones: StickyZoneJSON[];
  zonePositions: PositionSetJSON;
  comments: ModelCommentContentsJSON;
  descriptionPanels?: DescriptionPanelCollectionContentsJSON | null;
  fileContainers?: LoadableFileContainerCollectionContentsJSON | null;
  annotationLinks?: AnnotationLinkCollectionJSON | null;
  nodeDescriptions: ElementDescriptionSetJSON | undefined;
  zoneDescriptions: ElementDescriptionSetJSON | undefined;
  displayOrderTree: DisplayOrderTreeJSON | null;
};

export class StickyModelContents {
  public readonly type: ModelType = ModelType.Sticky;
  public readonly namespace: ModelNamespace = ModelNamespace.Balus;

  private constructor(
    public readonly key: ModelKey,
    private readonly version: number,
    private readonly nodes: NodeCollection,
    private readonly links: LinkCollection,
    private readonly zones: StickyZoneContents,
    private readonly comments: ModelCommentContents,
    private readonly descriptionPanels: DescriptionPanelCollectionContents,
    private readonly fileContainers: FileContainerCollectionContents,
    private readonly annotationLinks: AnnotationLinkCollection,
    private readonly nodeDescriptions: ElementDescriptionSet,
    private readonly zoneDescriptions: ElementDescriptionSet,
    private readonly displayOrderTree: DisplayOrderTree
  ) {}

  /**
   * 要素の集まりから、モデルとして不整合のないコンテンツオブジェクトを作成して返します。
   *
   * 特に、渡された要素で完結しないリンク要素があった場合、そのリンクは返されるコンテンツオブジェクトからは除外され、
   * コンテンツオブジェクトとは別に戻り値として返されます。
   * @param key
   * @param version
   * @param nodes
   * @param links
   * @param zones
   * @param zonePositions
   * @param comments
   * @param descriptionPanels
   * @param fileContainers
   * @param annotationLinks
   * @param nodeDescriptions
   * @param zoneDescriptions
   * @param displayOrderTree
   */
  static fromFragments(
    key: ModelKey,
    version: number,
    nodes: NodeCollection,
    links: LinkCollection,
    zones: StickyZoneCollection,
    zonePositions: PositionSet,
    comments: ModelCommentContents,
    descriptionPanels: DescriptionPanelCollectionContents,
    fileContainers: FileContainerCollectionContents,
    annotationLinks: AnnotationLinkCollection,
    nodeDescriptions: ElementDescriptionSet,
    zoneDescriptions: ElementDescriptionSet,
    displayOrderTree: DisplayOrderTree
  ): { contents: StickyModelContents; excludedLinks: LinkCollection } {
    // 始点・終点が渡されたnodes, zones に接続できるリンクだけを抽出
    const nodeIds = nodes.ids();
    const zoneIds = zones.ids();
    const nodeZoneIds = [...nodeIds, ...zoneIds];
    const [validLinks, excludedLinks] = links.splitByConnectivity(nodeZoneIds);

    // nodes, zones で構成されるDisplayOrderTree を引数のdisplayOrderTree から構築する
    // また、引数のdisplayOrderTreeに含まれていないノード、ゾーンは追加して不整合にならないようにする
    const partialTree = displayOrderTree
      .partialTree(nodeZoneIds)
      .addNodesNotIncluded(nodeIds)
      .addZonesNotIncluded(zoneIds);

    const contents = new StickyModelContents(
      key,
      version,
      nodes,
      validLinks,
      new StickyZoneContents(zones, zonePositions),
      comments,
      descriptionPanels,
      fileContainers,
      annotationLinks.validBy(
        [...fileContainers.allIds(), ...descriptionPanels.allIds()],
        [...nodes.keys(), ...zones.keys()]
      ),
      nodeDescriptions,
      zoneDescriptions,
      partialTree
    );

    return { contents, excludedLinks };
  }

  /**
   * モデルコンテンツの複製 (新しい識別子を発行して置き換えたもの) を返す。
   */
  cloneNew(assetUrlMap: AssetUrlMap, fileContainerAssetCopy?: FileContainerAssetCopy): StickyModelContents {
    const [newNodeCollection, nodeKeyMap] = this.nodes.cloneNew();
    const [newZones, zoneKeyMap, zoneIdMap] = this.zones.cloneNew();
    const newLinks = this.links.cloneNew({ ...nodeKeyMap, ...zoneKeyMap });
    const [newFileContainers, fileContainerIdMap] = this.fileContainers.cloneNew(fileContainerAssetCopy);
    const [newDescriptionPanels, descriptionPanelIdMap] = this.descriptionPanels.cloneNew(assetUrlMap);

    const nodeIdMap = Object.fromEntries(
      Object.entries(nodeKeyMap).map(([s, k]) => [new NodeKey(s).id.toString(), k.id.toString()])
    );

    const newModelKey = ModelKey.buildNew();

    return new StickyModelContents(
      newModelKey,
      this.version,
      newNodeCollection,
      newLinks,
      newZones,
      this.comments.cloneNew(newModelKey),
      newDescriptionPanels,
      newFileContainers,
      this.annotationLinks.cloneNew(
        { ...fileContainerIdMap, ...descriptionPanelIdMap },
        { ...nodeKeyMap, ...zoneKeyMap }
      ),
      this.nodeDescriptions.cloneNew(nodeKeyMap),
      this.zoneDescriptions.cloneNew(zoneKeyMap),
      this.displayOrderTree.cloneNew({ ...nodeIdMap, ...zoneIdMap })
    );
  }

  updateNodeZoneCreatedUserKey(userKey: UserKey): StickyModelContents {
    return new StickyModelContents(
      this.key,
      this.version,
      this.nodes.withCreatedUserKey(userKey),
      this.links,
      this.zones.withCreatedUserKey(userKey),
      this.comments,
      this.descriptionPanels,
      this.fileContainers,
      this.annotationLinks,
      this.nodeDescriptions,
      this.zoneDescriptions,
      this.displayOrderTree
    );
  }

  dump(): StickyModelContentsJSON {
    const {
      key,
      type,
      namespace,
      nodes,
      links,
      zones,
      version,
      comments,
      descriptionPanels,
      fileContainers,
      annotationLinks,
      nodeDescriptions,
      zoneDescriptions,
      displayOrderTree,
    } = this;

    return {
      key: key.toString(),
      type: type.toString(),
      namespace: namespace.toString(),
      nodes: nodes.dump(),
      links: links.dump(),
      zones: zones.zones.dump(),
      zonePositions: zones.positions.dump(),
      version,
      comments: comments.dump(),
      descriptionPanels: descriptionPanels.dump(),
      fileContainers: fileContainers.dump(),
      annotationLinks: annotationLinks.dump(),
      nodeDescriptions: nodeDescriptions.dump(),
      zoneDescriptions: zoneDescriptions.dump(),
      displayOrderTree: displayOrderTree.dump(),
    };
  }

  static load(dump: LoadableContentsJSON): StickyModelContents {
    const {
      key,
      type,
      namespace,
      version,
      nodes,
      links,
      zones,
      zonePositions,
      comments,
      descriptionPanels,
      fileContainers,
      annotationLinks,
      nodeDescriptions,
      zoneDescriptions,
      displayOrderTree,
    } = dump;

    if (
      (ModelType.Basic.toString() !== type && ModelType.Sticky.toString() !== type) ||
      ModelNamespace.Balus.toString() !== namespace
    ) {
      throw new Error(`Invalid type or namespace (type=${type}, namespace=${namespace}) in StickyModelContents`);
    }

    const modelKey = new ModelKey(key);
    const zoneCollection = StickyZoneCollection.load(zones || []);

    // 渡ってきた引数が信用できる（整合性が取れている）とは限らないので直接コンストラクタを呼ばない
    const { contents } = this.fromFragments(
      modelKey,
      version,
      NodeCollection.load(nodes || []),
      LinkCollection.load(links || []),
      zoneCollection,
      PositionSet.load(zonePositions || {}),
      comments ? ModelCommentContents.load(comments) : ModelCommentContents.buildEmpty(modelKey),
      DescriptionPanelCollectionContents.load(descriptionPanels || {}),
      FileContainerCollectionContents.load(fileContainers || {}),
      AnnotationLinkCollection.load(annotationLinks || []),
      ElementDescriptionSet.load(nodeDescriptions || {}),
      ElementDescriptionSet.load(zoneDescriptions || {}),
      displayOrderTree ? DisplayOrderTree.load(displayOrderTree) : new DisplayOrderTree()
    );

    return contents;
  }

  static buildEmpty(key: ModelKey, version: number): StickyModelContents {
    return new this(
      key,
      version,
      new NodeCollection(),
      new LinkCollection([]),
      StickyZoneContents.buildEmpty(),
      ModelCommentContents.buildEmpty(key),
      DescriptionPanelCollectionContents.buildEmpty(),
      FileContainerCollectionContents.buildEmpty(),
      AnnotationLinkCollection.buildEmpty(),
      ElementDescriptionSet.fromArray([]),
      ElementDescriptionSet.fromArray([]),
      new DisplayOrderTree()
    );
  }

  modelEntity(): StickyModel {
    const { key, version } = this;
    return new StickyModel({
      key,
      version,
      nodes: [],
      links: [],
      zones: [],
      zonePositions: new PositionSet(),
    });
  }

  nodeEntities(): StickyNode[] {
    return this.nodes.entities();
  }

  linkEntities(): LinkEntity[] {
    return this.links.entities();
  }

  zoneEntities(): StickyZone[] {
    return [...this.zones.zones.models];
  }

  zonePositionSet(): PositionSet {
    return this.zones.positions;
  }

  commentContents(): ModelCommentContents {
    return this.comments;
  }

  async saveTo(repository: StickyModelCascadeRepository): Promise<void> {
    return repository.saveContents(
      this.modelEntity(),
      this.nodeEntities(),
      this.linkEntities(),
      this.zoneEntities(),
      this.zonePositionSet(),
      this.commentContents(),
      this.descriptionPanels,
      this.fileContainers,
      this.annotationLinks,
      this.nodeDescriptions,
      this.zoneDescriptions,
      this.displayOrderTree
    );
  }

  /**
   * このモデルに含まれる要素の識別子(id)の一覧
   */
  elementIds(): ModelElementId[] {
    return [
      ...this.nodes.ids(),
      ...this.links.ids(),
      ...this.zones.allIds(),
      ...this.comments.allIds(),
      ...this.descriptionPanels.allIds(),
      ...this.fileContainers.allIds(),
      ...this.annotationLinks.allIds(),
    ];
  }

  minimizedElementIds(): ModelElementId[] {
    return this.displayOrderTree.minimizeElementIds([...this.nodes.ids(), ...this.zones.allIds()]);
  }

  isEqual(other: StickyModelContents): boolean {
    return (
      other instanceof StickyModelContents &&
      this.key.isEqual(other.key) &&
      this.version === other.version &&
      this.nodes.isEqual(other.nodes) &&
      this.links.isEqual(other.links) &&
      this.zones.isEqual(other.zones) &&
      this.comments.isEqual(other.comments) &&
      this.descriptionPanels.isEqual(other.descriptionPanels) &&
      this.fileContainers.isEqual(other.fileContainers) &&
      this.annotationLinks.isEqual(other.annotationLinks) &&
      this.displayOrderTree.isEqual(other.displayOrderTree)
    );
  }

  isEmpty(): boolean {
    const count =
      this.nodes.length +
      this.links.length +
      this.zones.count() +
      this.comments.count() +
      this.descriptionPanels.count() +
      this.fileContainers.count() +
      this.annotationLinks.count() +
      this.nodeDescriptions.size;

    return count === 0;
  }

  move(dx: number, dy: number): StickyModelContents {
    return new StickyModelContents(
      this.key,
      this.version,
      this.nodes.move(dx, dy),
      this.links,
      this.zones.move(dx, dy),
      this.comments.move(dx, dy),
      this.descriptionPanels.move(dx, dy),
      this.fileContainers.move(dx, dy),
      this.annotationLinks,
      this.nodeDescriptions,
      this.zoneDescriptions,
      this.displayOrderTree
    );
  }

  buildDescriptionPanelsCreateCommand(viewModelId: ViewModelId, modelId: ModelId): ICommand {
    return this.descriptionPanels.buildCreateCommand(viewModelId, modelId);
  }

  buildFileContainersCreateCommand(viewModelId: ViewModelId, modelId: ModelId): ICommand {
    return this.fileContainers.buildCreateCommand(viewModelId, modelId);
  }

  buildAnnotationLinksCreateCommand(viewModelId: ViewModelId, modelId: ModelId): ICommand {
    return this.annotationLinks.buildCreateCommand(viewModelId, modelId);
  }

  buildElementsCreateCommand(
    viewModel: ViewModelEntity,
    model: StickyModel,
    nodeEntityOperation: NodeEntityOperation,
    linkEntityOperation: LinkEntityOperation,
    zoneEntityOperation: StickyZoneEntityOperation,
    displayOrderRepository: DisplayOrderRepository,
    parentZoneIds?: StickyZoneId[]
  ): ICommand {
    return new ElementsCreateCommand(
      viewModel,
      model,
      nodeEntityOperation,
      linkEntityOperation,
      zoneEntityOperation,
      this.nodes,
      this.linkEntities(),
      this.zoneEntities(),
      this.zonePositionSet(),
      this.nodeDescriptions,
      this.zoneDescriptions,
      this.displayOrderTree,
      displayOrderRepository,
      parentZoneIds
    );
  }

  buildMoveCommand(
    dx: number,
    dy: number,
    positionMapRepository: StickyModelElementPositionMapRepository,
    commentPositionSetRepository: IPositionSetRepository,
    descriptionPanelPositionSetRepository: IPositionSetRepository,
    fileContainerPositionSetRepository: IPositionSetRepository
  ): ICommand | null {
    const moveNodeZoneCommand = this.buildMoveNodeZoneCommand(dx, dy, positionMapRepository);
    const moveCommentCommand = this.comments.buildMoveCommand(dx, dy, commentPositionSetRepository);
    const moveDescriptionPanelCommand = this.descriptionPanels.buildMoveCommand(
      dx,
      dy,
      descriptionPanelPositionSetRepository
    );
    const moveFileContainerCommand = this.fileContainers.buildMoveCommand(dx, dy, fileContainerPositionSetRepository);

    return CompositeCommand.composeOptionalCommands(
      moveNodeZoneCommand,
      moveCommentCommand,
      moveDescriptionPanelCommand,
      moveFileContainerCommand
    );
  }

  private buildMoveNodeZoneCommand(
    dx: number,
    dy: number,
    positionMapRepository: StickyModelElementPositionMapRepository
  ): ICommand | null {
    const { nodes, zones } = this;
    const zonePositions = zones.positions;

    if (zonePositions.isEmpty() && nodes.isEmpty()) return null;

    const positions = new ModelElementPositionMap(nodes.positionSet(), zonePositions);

    return new ElementsMoveCommand(positions, positions.movePosition(dx, dy), positionMapRepository);
  }

  /**
   * 外部向けに付箋のテキストを文字列として出力します。
   */
  exportNodesText(): string {
    return this.nodes.exportTexts();
  }

  /**
   * 付箋モデル中に含まれるview-model-assetsのURL一覧
   */
  assetUrls(): string[] {
    const urls = [...this.descriptionPanels.assetUrls()];
    return Array.from(new Set(urls));
  }

  fileContainerAssetIds(): ViewModelAssetId[] {
    return this.fileContainers.assetIds();
  }

  getUnionRect(): Rect | null {
    const { nodes, zones, descriptionPanels, fileContainers, comments } = this;
    if (
      nodes.isEmpty() &&
      zones.count() === 0 &&
      descriptionPanels.count() === 0 &&
      fileContainers.count() === 0 &&
      comments.count() === 0
    )
      return null;

    // ふせんかゾーンがある場合には、ふせんorゾーンのみで矩形を作る
    if (!nodes.isEmpty() || !(zones.count() === 0)) {
      const rects = [
        ...nodes.map((node) => node.getRect()),
        ...zones.map(([zone, position]) => {
          return zone.getRect(position);
        }),
      ];

      return rects.reduce((result, rect) => result.union(rect), rects[0]);
    }

    const descriptionPanelRects = descriptionPanels
      .allIds()
      .map((panelId) => descriptionPanels.getRect(panelId))
      .filter((rect): rect is Rect => !!rect);
    const fileContainerRects = fileContainers
      .allIds()
      .map((id) => fileContainers.getRect(id))
      .filter((rect): rect is Rect => !!rect);
    const commentRects = comments
      .allIds()
      .map((commentId) => comments.getPosition(commentId))
      .filter((point): point is Point => !!point)
      .map((point) => new Rect(point, new Size(CommentConstants.UISetting.width, 96))); // コメントはサイズを保存していないので、貼り付け用の矩形を指定するために仮の高さを指定
    const rects = [...descriptionPanelRects, ...fileContainerRects, ...commentRects];
    if (rects.length <= 0) return null;

    return rects.reduce((result, rect) => result.union(rect), rects[0]);
  }

  getTopZone(): DisplayOrderTreeZone | undefined {
    return this.displayOrderTree.zones[0];
  }

  findLowestDescendantZoneId(zoneIds: StickyZoneId[]): StickyZoneId | null {
    return this.displayOrderTree.findLowestDescendantZoneId(zoneIds);
  }
}
